import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createInventory, getInventories, getSAInventories, getInventoryId, getInventoryDetail, getInventoryWebDetail, deleteInventory, updateInventory, getInventoryOptions, getInventorySettings, updateInventorySettings, printInventory } from "../api.js";
import { useNavigate } from "react-router-dom";
import { updateInventoryExpense, deleteInventoryExpense, addInventoryExpense } from '../api.js';
import { useNotification } from "../../../contexts/NotificationContext";

export const useGetInventoryId = (id, submitting) => {
  const { data, isLoading, isError, error, isSuccess } = useQuery({
    queryKey: ['inventory', id],
    queryFn: () => getInventoryId(id),
    enabled: !!id && !!submitting,
  });

  return { data, isLoading, isSuccess, isError, error };
};
export const useGetInventories = (search, status, location, altLocation, page, pageSize, tableFilters, enabled = true) => {
  const statusKey = Array.isArray(status)
    ? [...status].map(String).sort().join(",")
    : String(status ?? "");
  const { data, isLoading, isError, error, isSuccess } = useQuery({
    queryKey: ['inventories', { search, statusKey, location, altLocation, page, pageSize, tableFilters }],
    queryFn: ({ signal }) => getInventories(search, status, location, altLocation, page, pageSize, { signal }, tableFilters),
    enabled,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
    refetchOnMount: false,
  });

  return { data, isLoading, isSuccess, isError, error };
};

export const useGetSAInventories = (search, status, location, altLocation, page, pageSize, tableFilters, tenant) => {
  const { data, isLoading, isError, error, isSuccess } = useQuery({
    queryKey: ['sa-inventories', { search, status, location, altLocation, page, pageSize, tableFilters, tenant }],
    queryFn: ({ signal }) => getSAInventories(search, status, location, altLocation, page, pageSize, { signal }, tableFilters, tenant),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 dakika cache'de taze tut
    refetchOnMount: false,
  });

  return { data, isLoading, isSuccess, isError, error };
};

export const useDeleteInventory = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ id }) => deleteInventory(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["inventories"] });

      // Show success notification
      showNotification('success', 'Inventory deleted successfully!');

      // Navigate to inventory list
      navigate('/search-inventory');
    },
    onError: (error) => {
      console.error("Error deleting inventory:", error);

      // Show error notification
      let errorMessage = 'Error deleting inventory. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useCreateInventory = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const mutation = useMutation({
    mutationFn: (data) => createInventory(data),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["inventories"] });
      await queryClient.invalidateQueries({ queryKey: ["inventory-detail"] });

      // Show success notification
      showNotification('success', 'Inventory created successfully!');

      // Navigate immediately without setTimeout
      navigate(`/inventory/${data?.documentId}`);
    },
    onError: (error) => {
      console.error("Error creating inventory:", error);

      // Show error notification
      let errorMessage = 'Error creating inventory. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });

  return mutation;
};

export const useGetInventoryDetail = (id, tab) => {
  const { data, isLoading, isError, error, isSuccess, isFetching } = useQuery({
    queryKey: ["inventory-detail", id, tab],
    queryFn: () => getInventoryDetail(id, tab),
    enabled: !!id && !!tab,
    staleTime: 1000 * 60 * 5, // 5 dakika cache
    cacheTime: 1000 * 60 * 10, // 10 dakika cache'de tut
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Mount olduğunda refetch yapma
  });
  return { data, isLoading, isSuccess, isError, error, isFetching };
};

export const useGetInventoryWebDetail = (id) => {
  const { data, isLoading, isError, error, isSuccess, isFetching } = useQuery({
    queryKey: ["inventory-web-detail", id],
    queryFn: () => getInventoryWebDetail(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  });

  return { data, isLoading, isSuccess, isError, error, isFetching };
};

export const useUpdateInventory = (tenantId) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ id, data }) => updateInventory(id, data, tenantId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["inventory-detail"] });
      await queryClient.invalidateQueries({ queryKey: ["calculus-detail"] });

      // Show success notification
      showNotification('success', 'Inventory updated successfully!');
    },
    onError: (error) => {
      console.error("Error updating inventory:", error);

      // Show error notification
      let errorMessage = 'Error updating inventory. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useUpdateInventoryExpense = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ documentId, data }) => updateInventoryExpense(documentId, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["inventory-detail"] });

      // Show success notification
      showNotification('success', 'Expense updated successfully!');
    },
    onError: (error) => {
      console.error("Error updating expense:", error);

      // Show error notification
      let errorMessage = 'Error updating expense. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useDeleteInventoryExpense = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ id }) => deleteInventoryExpense(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["inventory-detail"] });

      // Show success notification
      showNotification('success', 'Expense deleted successfully!');
    },
    onError: (error) => {
      console.error("Error deleting expense:", error);

      // Show error notification
      let errorMessage = 'Error deleting expense. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useAddInventoryExpense = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ documentId, data }) => addInventoryExpense(documentId, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["inventory-detail"] });

      // Show success notification
      showNotification('success', 'Expense added successfully!');
    },
    onError: (error) => {
      console.error("Error adding expense:", error);

      // Show error notification
      let errorMessage = 'Error adding expense. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};


export const useGetInventorySettings = () => {
  const { data, isLoading, isError, error, isSuccess } = useQuery({
    queryKey: ['inventory-settings'],
    queryFn: () => getInventorySettings(),
    staleTime: 1000 * 60 * 5, // 5 dakika cache
    cacheTime: 1000 * 60 * 10, // 10 dakika cache'de tut
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Mount olduğunda refetch yapma
  });

  return { data, isLoading, isSuccess, isError, error };
};

export const useUpdateInventorySettings = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ id, data }) => updateInventorySettings(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["inventory-settings"] });

      // Show success notification
      showNotification('success', 'Inventory settings updated successfully!');
    },
    onError: (error) => {
      console.error("Error updating inventory settings:", error);

      // Show error notification
      let errorMessage = 'Error updating inventory settings. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const usePrintInventory = () => {
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ id, documents }) => printInventory(id, documents),
    onSuccess: (response) => {
      showNotification('success', 'PDF loaded successfully!');
      return response;
    },
    onError: (error) => {
      console.error("Error printing inventory:", error);
      showNotification('danger', 'Error generating PDF. Please try again.');
    },
  });
};